import React, { ReactElement } from 'react';
import { Button, Modal } from 'elmo-elements';
import { LinkedCourse } from '@/modules/types/elearning/moduleDeletionBlocked';
import './CannotDeleteModuleModal.scss';

// Max courses shown at once (LMS2-4947 AC 3.1/3.2). Remaining courses surface
// naturally as the user delinks and reattempts deletion.
const MAX_COURSES_SHOWN = 3;

const BLOCKED_BODY =
  'This module is currently used in one or more courses and cannot be deleted. ' +
  'Remove the module from the course(s) below before reattempting.';

const COURSES_UNAVAILABLE_MESSAGE =
  'The list of linked courses could not be loaded. Please try again or contact your administrator.';

// Only render a course name as a link when its URL is an http(s) URL. The URL
// comes from TMS (relayed via ELA); defense-in-depth against a malicious or
// misconfigured value (e.g. `javascript:`/`data:`) becoming a clickable vector.
const isSafeHttpUrl = (url: string): boolean => {
  try {
    return ['http:', 'https:'].includes(new URL(url).protocol);
  } catch {
    return false;
  }
};

export interface CannotDeleteModuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  courses: LinkedCourse[];
  coursesUnavailable: boolean;
}

const CannotDeleteModuleModal = ({
  isOpen,
  onClose,
  courses,
  coursesUnavailable,
}: CannotDeleteModuleModalProps): ReactElement => {
  // Either the whole list is unavailable, or we have courses to show.
  const showError = coursesUnavailable || courses.length === 0;
  const visibleCourses = courses.slice(0, MAX_COURSES_SHOWN);

  return (
    <Modal
      id="CannotDeleteModuleDialog"
      type="small"
      isOpened={isOpen}
      closeModal={onClose}
      title="Cannot Delete Module"
      closeLabel="Close"
      primaryButton={
        <Button type="default" isUppercase={false} onClick={onClose}>
          Close
        </Button>
      }
    >
      {showError ? (
        <p className="cannot-delete-module__message">
          {COURSES_UNAVAILABLE_MESSAGE}
        </p>
      ) : (
        <>
          <p className="cannot-delete-module__message">{BLOCKED_BODY}</p>
          <ul className="cannot-delete-module__list">
            {visibleCourses.map((course) => (
              <li key={course.id} className="cannot-delete-module__item">
                {course.settingsUrl && isSafeHttpUrl(course.settingsUrl) ? (
                  <a
                    href={course.settingsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {course.name}
                  </a>
                ) : (
                  course.name
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </Modal>
  );
};

export default CannotDeleteModuleModal;
